import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ConnectionState,
  DisconnectReason,
  type RemoteTrackPublication,
  LocalVideoTrack,
  RemoteVideoTrack,
  Room,
  RoomEvent,
  Track,
} from 'livekit-client';
import { ShareButton } from './ShareButton';
import { VideoTile } from './VideoTile';
import { DiscordMark, Voice } from './icons';
import { Faces } from './Faces';
import { ScreenAudio } from './ScreenAudio';
import { ListaDeTelas, type Transmissor } from './ListaDeTelas';

type Status =
  | { kind: 'loading' }
  | { kind: 'needs-login' }
  | { kind: 'not-in-voice'; message: string }
  | { kind: 'kicked'; title: string; message: string }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; room: Room; roomName: string };

interface TokenResponse {
  token: string;
  url: string;
  room: string;
  roomName: string;
  identity: string;
}

interface Screen {
  key: string;
  identity: string;
  track: LocalVideoTrack | RemoteVideoTrack;
  name: string;
  isLocal: boolean;
}

export function RoomView({ channelId }: { channelId: string }) {
  const [status, setStatus] = useState<Status>({ kind: 'loading' });
  // Identity de quem voce escolheu assistir; null = todas.
  const [selecionado, setSelecionado] = useState<string | null>(null);
  // Eventos do LiveKit mutam objetos vivos; um contador forca o re-render.
  const [revision, bump] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    let cancelled = false;
    const room = new Room({
      // Assinante so recebe a camada do tamanho que esta realmente vendo.
      adaptiveStream: true,
      // Servidor para de encaminhar camadas que ninguem esta olhando.
      dynacast: true,
    });

    /*
     * Quando a pessoa sai do canal de voz, o bot manda o LiveKit remove-la da
     * sala. Sem tratar isso aqui, a tela congelaria dizendo "reconectando" —
     * ela sairia do video sem entender por que.
     */
    const onDisconnected = (reason?: DisconnectReason) => {
      // Desconexao nossa, ao trocar de pagina: nao e para avisar nada.
      if (cancelled || reason === DisconnectReason.CLIENT_INITIATED) return;

      if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
        setStatus({
          kind: 'kicked',
          title: 'Voce saiu do canal de voz',
          message:
            'A transmissao so fica disponivel para quem esta na call. Entre no canal de voz de novo para voltar a ver.',
        });
      } else if (reason === DisconnectReason.DUPLICATE_IDENTITY) {
        setStatus({
          kind: 'kicked',
          title: 'Aberto em outro lugar',
          message:
            'Voce abriu esta sala em outra aba ou dispositivo. So uma conexao por pessoa e permitida.',
        });
      } else {
        setStatus({
          kind: 'kicked',
          title: 'Conexao encerrada',
          message: 'A sala foi fechada ou a conexao caiu.',
        });
      }
    };
    room.on(RoomEvent.Disconnected, onDisconnected);

    const events = [
      RoomEvent.TrackSubscribed,
      RoomEvent.TrackUnsubscribed,
      RoomEvent.LocalTrackPublished,
      RoomEvent.LocalTrackUnpublished,
      RoomEvent.ParticipantConnected,
      RoomEvent.ParticipantDisconnected,
      RoomEvent.ConnectionStateChanged,
      // O avatar viaja no metadata; se ele chegar depois do join, a roda de
      // fotos precisa se redesenhar.
      RoomEvent.ParticipantMetadataChanged,
      // Sem este, uma faixa de som que chega depois do video nao seria notada.
      RoomEvent.AudioPlaybackStatusChanged,
    ] as const;
    for (const e of events) room.on(e, bump);

    void (async () => {
      try {
        const res = await fetch('/api/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ room: channelId }),
        });

        if (res.status === 401) {
          setStatus({ kind: 'needs-login' });
          return;
        }
        if (res.status === 403) {
          const body = (await res.json().catch(() => ({}))) as { message?: string };
          setStatus({
            kind: 'not-in-voice',
            message: body.message ?? 'Voce nao esta no canal de voz desta sala.',
          });
          return;
        }
        if (!res.ok) throw new Error(`servidor respondeu ${res.status}`);

        const data = (await res.json()) as TokenResponse;
        if (cancelled) return;

        await room.connect(data.url, data.token);
        if (cancelled) {
          await room.disconnect();
          return;
        }
        setStatus({ kind: 'ready', room, roomName: data.roomName });
      } catch (err) {
        if (!cancelled) {
          setStatus({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
        }
      }
    })();

    return () => {
      cancelled = true;
      room.off(RoomEvent.Disconnected, onDisconnected);
      for (const e of events) room.off(e, bump);
      void room.disconnect();
    };
  }, [channelId]);

  const screens: Screen[] = useMemo(() => {
    const room = status.kind === 'ready' ? status.room : null;
    if (!room) return [];

    const out: Screen[] = [];

    for (const pub of room.localParticipant.trackPublications.values()) {
      if (pub.source === Track.Source.ScreenShare && pub.track) {
        out.push({
          key: pub.trackSid,
          identity: room.localParticipant.identity,
          track: pub.track as LocalVideoTrack,
          name: room.localParticipant.name ?? 'Voce',
          isLocal: true,
        });
      }
    }

    for (const participant of room.remoteParticipants.values()) {
      for (const pub of participant.trackPublications.values()) {
        if (pub.source === Track.Source.ScreenShare && pub.track && pub.isSubscribed) {
          out.push({
            key: pub.trackSid,
            identity: participant.identity,
            track: pub.track as RemoteVideoTrack,
            name: participant.name ?? participant.identity,
            isLocal: false,
          });
        }
      }
    }

    return out;
    // `revision` entra na lista para reavaliar quando o LiveKit muta os objetos.
  }, [status, revision]);

  /*
   * Quem esta transmitindo, esteja voce assistindo ou nao.
   *
   * Nao da para derivar de `screens`: ao cancelar a assinatura de alguem, a
   * faixa some dali — e a pessoa sumiria da lista lateral justamente por voce
   * ter deixado de assistir, tornando impossivel voltar para ela.
   */
  const transmissores: Transmissor[] = useMemo(() => {
    const room = status.kind === 'ready' ? status.room : null;
    if (!room) return [];

    const lerAvatar = (metadata?: string): string | null => {
      if (!metadata) return null;
      try {
        return (JSON.parse(metadata) as { avatar?: string }).avatar ?? null;
      } catch {
        return null;
      }
    };

    const out: Transmissor[] = [];
    for (const pub of room.localParticipant.trackPublications.values()) {
      if (pub.source === Track.Source.ScreenShare) {
        out.push({
          identity: room.localParticipant.identity,
          nome: room.localParticipant.name ?? 'Voce',
          avatar: lerAvatar(room.localParticipant.metadata),
          isLocal: true,
        });
      }
    }
    for (const p of room.remoteParticipants.values()) {
      for (const pub of p.trackPublications.values()) {
        if (pub.source === Track.Source.ScreenShare) {
          out.push({
            identity: p.identity,
            nome: p.name ?? p.identity,
            avatar: lerAvatar(p.metadata),
            isLocal: false,
          });
        }
      }
    }
    return out;
  }, [status, revision]);

  /*
   * Cancela de verdade o que voce nao esta assistindo.
   *
   * Sem isto, esconder um quadro apenas o tiraria da tela: o navegador
   * continuaria baixando o video. Com tres pessoas em 1080p, seriam 24 Mb/s
   * de download para ver 8.
   */
  useEffect(() => {
    if (status.kind !== 'ready') return;
    for (const p of status.room.remoteParticipants.values()) {
      const querVer = selecionado === null || selecionado === p.identity;
      for (const pub of p.trackPublications.values()) {
        const ehTela =
          pub.source === Track.Source.ScreenShare ||
          pub.source === Track.Source.ScreenShareAudio;
        if (!ehTela) continue;
        const remota = pub as RemoteTrackPublication;
        if (remota.isSubscribed !== querVer) remota.setSubscribed(querVer);
      }
    }
  }, [status, selecionado, revision]);

  // Se a pessoa que voce assistia parar de transmitir, volta para todas em vez
  // de deixar a tela vazia sem explicacao.
  useEffect(() => {
    if (selecionado && !transmissores.some((t) => t.identity === selecionado)) {
      setSelecionado(null);
    }
  }, [transmissores, selecionado]);

  /*
   * Ao comecar a transmitir, pula para a propria tela.
   *
   * Sem isto, quem esta assistindo outra pessoa clica em compartilhar, escolhe
   * a janela — e nada muda na tela, porque a selecao continua na outra pessoa.
   * A conclusao natural e que o compartilhamento falhou, quando na verdade ele
   * esta no ar para todos os outros.
   */
  const euTransmitia = useRef(false);
  useEffect(() => {
    const eu = transmissores.find((t) => t.isLocal);
    if (eu && !euTransmitia.current) setSelecionado(eu.identity);
    euTransmitia.current = Boolean(eu);
  }, [transmissores]);

  if (status.kind === 'loading') {
    return (
      <Centered>
        <p className="empty-text">Conectando&hellip;</p>
      </Centered>
    );
  }

  if (status.kind === 'needs-login') {
    const to = encodeURIComponent(`/r/${channelId}`);
    return (
      <Centered>
        <p className="empty-text">Entre com o Discord para abrir a sala.</p>
        <a className="btn btn-primary btn-lg" href={`/auth/discord?to=${to}`}>
          <DiscordMark size={18} />
          Entrar com Discord
        </a>
      </Centered>
    );
  }

  if (status.kind === 'not-in-voice') {
    return (
      <Centered>
        <h2 className="empty-title">Sala fechada</h2>
        <p className="empty-text">{status.message}</p>
        <button className="btn" onClick={() => location.reload()}>
          Ja entrei, tentar de novo
        </button>
      </Centered>
    );
  }

  if (status.kind === 'kicked') {
    return (
      <Centered>
        <h2 className="empty-title">{status.title}</h2>
        <p className="empty-text">{status.message}</p>
        <button className="btn btn-primary" onClick={() => location.reload()}>
          Ja voltei, tentar de novo
        </button>
      </Centered>
    );
  }

  if (status.kind === 'error') {
    return (
      <Centered>
        <h2 className="empty-title">Nao consegui conectar</h2>
        <p className="error">{status.message}</p>
        <button className="btn" onClick={() => location.reload()}>
          Tentar de novo
        </button>
      </Centered>
    );
  }

  const { room, roomName } = status;
  const connecting = room.state !== ConnectionState.Connected;
  const visible = selecionado
    ? screens.filter((s) => s.identity === selecionado)
    : screens;

  return (
    <div className="room">
      <ScreenAudio room={room} />

      <header className="topbar">
        <div className="topbar-title">
          <Voice size={20} />
          <span className="topbar-name">{roomName}</span>
          <Faces room={room} />
          {connecting && <span className="topbar-meta">reconectando</span>}
        </div>
        <ShareButton room={room} />
      </header>

      <div className="palco">
        <ListaDeTelas
          transmissores={transmissores}
          selecionado={selecionado}
          onSelecionar={setSelecionado}
        />

        {visible.length === 0 ? (
          <Centered>
            <h2 className="empty-title">
              {transmissores.length > 0 ? 'Carregando a transmissao…' : 'Ninguem transmitindo'}
            </h2>
            {transmissores.length === 0 && (
              <p className="empty-text">
                Use <strong>Compartilhar tela</strong> no topo.
              </p>
            )}
          </Centered>
        ) : (
          <main className="grid" data-count={Math.min(visible.length, 4)}>
            {visible.map((s) => (
              <VideoTile
                key={s.key}
                track={s.track}
                name={s.name}
                isLocal={s.isLocal}
                focused={selecionado === s.identity}
                onToggleFocus={() =>
                  setSelecionado((cur) => (cur === s.identity ? null : s.identity))
                }
              />
            ))}
          </main>
        )}
      </div>
    </div>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="centered">{children}</div>;
}
